/**
 * Composant wrapper pour les pages de widgets
 *
 * Fournit le contexte global nécessaire aux widgets :
 * - HelmetProvider pour la gestion des métadonnées
 * - SettingsProvider pour les paramètres d'affichage
 * - ThemeProvider pour le thème Material-UI
 * - SnackbarProvider pour les notifications
 *
 * Extrait automatiquement companyId et apiKey des paramètres URL
 * et les injecte dans les composants enfants.
 *
 * @module WidgetPage
 */

import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {useSearchParams} from 'react-router-dom';
import {HelmetProvider} from 'react-helmet-async';
import {SnackbarProvider} from 'packages/common/components/snackbar';
import {SettingsProvider} from 'packages/common/components/settings';
import ThemeProvider from 'packages/common/theme';

/**
 * Configuration par défaut du thème pour les widgets
 * @constant {Object}
 */
const DEFAULT_SETTINGS = {
  themeMode: 'light',
  themeDirection: 'ltr',
  themeContrast: 'default',
  themeLayout: 'vertical',
  themeColorPresets: 'default',
};

/**
 * Wrapper de page fournissant les providers nécessaires aux widgets
 *
 * @param {Object} props - Props du composant
 * @param {React.ReactElement} props.children - Composant widget enfant
 * @return {JSX.Element} Page wrapper avec tous les providers
 *
 * @example
 * <WidgetPage>
 *   <TableReservationView mode="fixed" />
 * </WidgetPage>
 */
export default function WidgetPage({children}) {
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('companyId');
  const apiKey = searchParams.get('apiKey');

  // Gestionnaire ESC pour fermer le modal parent quand le widget est dans une iframe
  useEffect(() => {
    // Vérifier si on est dans une iframe
    const isInIframe = window.self !== window.top;
    if (!isInIframe) return;

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        // Envoyer un message au parent pour fermer le modal
        window.parent.postMessage({type: 'brydge-close-modal'}, '*');
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, []);

  return (
    <HelmetProvider>
      <SettingsProvider defaultSettings={DEFAULT_SETTINGS}>
        <ThemeProvider>
          <SnackbarProvider>
            {React.cloneElement(children, {
              companyId: companyId || '',
              apiKey: apiKey || '',
            })}
          </SnackbarProvider>
        </ThemeProvider>
      </SettingsProvider>
    </HelmetProvider>
  );
}

WidgetPage.propTypes = {
  /**
   * Composant widget enfant qui recevra les props companyId et apiKey
   */
  children: PropTypes.element.isRequired,
};
